import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { AgentStatus } from '../../../shared/api.js';
import { formatDuration, formatUsd, prettyJson, shortToolName } from '../lib/format';
import type { ApprovalState, ChatItem, ToolItem } from './state';
import { useChat, type ConnectionState } from './useChat';

const APPROVAL_LABEL: Record<ApprovalState, string> = {
  pending: 'Ждёт подтверждения',
  approved: 'Разрешено',
  declined: 'Отклонено',
  timeout: 'Время ожидания истекло',
  interrupted: 'Прервано',
  'session closed': 'Сессия закрыта',
};

const CONNECTION_LABEL: Record<ConnectionState, string> = {
  idle: 'не подключено',
  connecting: 'подключение…',
  open: 'онлайн',
  reconnecting: 'переподключение…',
  closed: 'сессия закрыта',
};

const SUGGESTIONS = [
  'Какое сегодня число?',
  'Что у меня в понедельник?',
  'Найди дубли на этой неделе',
];

function MarkdownText({ text }: { text: string }) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ node: _node, ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer" />
        ),
        table: ({ node: _node, ...props }) => (
          <div className="table-scroll">
            <table {...props} />
          </div>
        ),
      }}
    >
      {text}
    </Markdown>
  );
}

function ToolCard({
  item,
  onDecision,
}: {
  item: ToolItem;
  onDecision: (requestId: string, allow: boolean) => void;
}) {
  const approval = item.approval;
  const pending = approval?.state === 'pending';
  const status = item.result
    ? item.result.isError
      ? 'error'
      : 'done'
    : approval && approval.state !== 'approved'
      ? approval.state === 'pending'
        ? 'pending'
        : 'error'
      : 'running';

  return (
    <div className={`tool-card ${pending ? 'tool-card--attention' : ''}`}>
      <details open={pending}>
        <summary>
          <span className={`dot dot--${status}`} aria-hidden />
          <code>{shortToolName(item.name)}</code>
          {approval && (
            <span
              className={`pill pill--${approval.state === 'approved' ? 'ok' : pending ? 'warn' : 'muted'}`}
            >
              {APPROVAL_LABEL[approval.state]}
            </span>
          )}
        </summary>
        {approval?.title && <p className="tool-card__title">{approval.title}</p>}
        <div className="tool-card__label">Параметры</div>
        <pre>{prettyJson(item.input)}</pre>
        {item.result && (
          <>
            <div className="tool-card__label">{item.result.isError ? 'Ошибка' : 'Результат'}</div>
            <pre className={item.result.isError ? 'pre--error' : undefined}>{item.result.text}</pre>
          </>
        )}
      </details>
      {pending && approval && (
        <div className="tool-card__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onDecision(approval.requestId, true)}
          >
            Разрешить
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => onDecision(approval.requestId, false)}
          >
            Отклонить
          </button>
        </div>
      )}
    </div>
  );
}

function ChatMessage({
  item,
  onDecision,
}: {
  item: ChatItem;
  onDecision: (requestId: string, allow: boolean) => void;
}) {
  switch (item.kind) {
    case 'user':
      return <div className="msg msg--user">{item.text}</div>;
    case 'assistant':
      return (
        <div className={`msg msg--assistant ${item.streaming ? 'msg--streaming' : ''}`}>
          <MarkdownText text={item.text} />
        </div>
      );
    case 'tool':
      return <ToolCard item={item} onDecision={onDecision} />;
    case 'notice':
      return <div className={`notice notice--${item.tone}`}>{item.text}</div>;
  }
}

function NotConfigured({ status }: { status: AgentStatus }) {
  return (
    <div className="chat__empty">
      <h3>Чат не настроен</h3>
      <p>
        Для чата нужен ключ Anthropic API. Добавьте в <code>.env</code> и перезапустите веб-сервер:
      </p>
      <ul className="var-list">
        {status.missing.map((name) => (
          <li key={name}>
            <code>{name}</code>
          </li>
        ))}
        {status.invalid.map((problem) => (
          <li key={problem} className="text-error">
            {problem}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ChatPanel({ status }: { status: AgentStatus | undefined }) {
  const enabled = Boolean(status?.configured);
  const chat = useChat(enabled);
  const { state } = chat;
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [state.items]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (el) stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const submit = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || sending || state.busy) return;
    setSending(true);
    stickToBottom.current = true;
    try {
      await chat.send(trimmed);
      setDraft('');
    } catch {
      /* error is shown by the hook */
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit(draft);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void submit(draft);
    }
  };

  const onDecision = (requestId: string, allow: boolean) => void chat.approve(requestId, allow);

  if (!status) {
    return (
      <section className="chat">
        <div className="chat__empty">Загрузка…</div>
      </section>
    );
  }

  return (
    <section className="chat" aria-label="Чат с агентом">
      <header className="chat__header">
        <div>
          <h2>Агент</h2>
          <div className="chat__meta">
            {enabled ? (
              <>
                <span
                  className={`dot dot--${chat.connection === 'open' ? 'done' : 'pending'}`}
                  aria-hidden
                />
                <span>
                  {[
                    CONNECTION_LABEL[chat.connection],
                    state.model,
                    state.lastTurn && formatDuration(state.lastTurn.durationMs),
                    state.lastTurn && formatUsd(state.lastTurn.totalCostUsd),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </>
            ) : (
              'выключен'
            )}
          </div>
        </div>
        {enabled && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void chat.newChat()}
            disabled={state.busy}
          >
            Новый чат
          </button>
        )}
      </header>

      {!enabled ? (
        <NotConfigured status={status} />
      ) : (
        <>
          <div className="chat__scroll" ref={scrollRef} onScroll={onScroll}>
            {state.items.length === 0 ? (
              <div className="chat__empty">
                <p>
                  Спросите про календарь. Операции записи агент выполнит только после вашего
                  подтверждения.
                </p>
                <div className="suggestions">
                  {SUGGESTIONS.map((text) => (
                    <button
                      key={text}
                      type="button"
                      className="chip"
                      onClick={() => void submit(text)}
                      disabled={chat.connection !== 'open'}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              state.items.map((item) => (
                <ChatMessage key={item.key} item={item} onDecision={onDecision} />
              ))
            )}
            {state.busy && state.items.at(-1)?.kind !== 'assistant' && (
              <div className="typing" aria-label="Агент думает">
                <span />
                <span />
                <span />
              </div>
            )}
            {state.busy && state.retry && (
              <div className="notice notice--warning" role="status">
                API не отвечает ({state.retry.status ?? 'нет ответа'}, {state.retry.error}). Повтор{' '}
                {state.retry.attempt} из {state.retry.maxRetries} через{' '}
                {formatDuration(state.retry.delayMs)}.
              </div>
            )}
          </div>

          {chat.error && <div className="notice notice--error chat__error">{chat.error}</div>}

          <form className="composer" onSubmit={onSubmit}>
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                state.closed
                  ? 'Сессия закрыта — начните новый чат'
                  : 'Сообщение… (Enter — отправить)'
              }
              rows={2}
              maxLength={8000}
              disabled={state.closed}
            />
            {state.busy ? (
              <button type="button" className="btn" onClick={() => void chat.interrupt()}>
                Стоп
              </button>
            ) : (
              <button
                type="submit"
                className="btn btn--primary"
                disabled={!draft.trim() || sending || state.closed || chat.connection !== 'open'}
              >
                Отправить
              </button>
            )}
          </form>
        </>
      )}
    </section>
  );
}
